import { batch, exec, query } from './client'
import type { InstrumentDetail, InstrumentType } from '../api/types'

const INSERT_SQL = `
  INSERT OR REPLACE INTO instruments (
    symbol, type, trading, fractional_trading, option_trading,
    option_spread_trading, shorting_availability, raw
  ) VALUES (?, ?, ?, ?, ?, ?, ?, ?)
`

// In-memory mirror of everything upserted this session. Search works from it
// immediately on mobile where OPFS-SAH often falls back to :memory: and the
// SQLite table starts empty, and it bridges the (small) insert latency after
// each remote search lands.
const memCache = new Map<string, InstrumentDetail>()

interface Row {
  symbol: string
  type: string
  trading: string | null
  fractional_trading: string | null
  option_trading: string | null
  option_spread_trading: string | null
  shorting_availability: string | null
  raw: string
}

function rowToInstrument(r: Row): InstrumentDetail {
  let name: string | undefined
  try {
    name = (JSON.parse(r.raw) as InstrumentDetail).instrument?.name
  } catch {
    name = undefined
  }
  return {
    instrument: { symbol: r.symbol, type: r.type as InstrumentType, name },
    trading: r.trading ?? '',
    fractionalTrading: r.fractional_trading ?? '',
    optionTrading: r.option_trading ?? '',
    optionSpreadTrading: r.option_spread_trading ?? '',
    shortingAvailability: r.shorting_availability ?? undefined,
  }
}

/**
 * Write-through for remote (Schwab) search results: the cache accumulates
 * whatever has been searched so repeat lookups resolve locally/offline.
 * There is no full-universe sync anymore — Schwab has no such endpoint.
 */
export async function upsertInstruments(items: InstrumentDetail[]): Promise<void> {
  if (items.length === 0) return
  for (const it of items) memCache.set(it.instrument.symbol, it)
  const statements = items.map((it) => ({
    sql: INSERT_SQL,
    params: [
      it.instrument.symbol,
      it.instrument.type,
      it.trading ?? null,
      it.fractionalTrading ?? null,
      it.optionTrading ?? null,
      it.optionSpreadTrading ?? null,
      it.shortingAvailability ?? null,
      JSON.stringify(it),
    ],
  }))
  await batch(statements)
}

function searchMemCache(
  trimmed: string,
  opts: { type?: InstrumentType; limit?: number },
): InstrumentDetail[] {
  const limit = opts.limit ?? 20
  const matches: InstrumentDetail[] = []
  for (const it of memCache.values()) {
    if (!it.instrument.symbol.startsWith(trimmed)) continue
    if (opts.type && it.instrument.type !== opts.type) continue
    matches.push(it)
  }
  matches.sort((a, b) => {
    const la = a.instrument.symbol.length
    const lb = b.instrument.symbol.length
    if (la !== lb) return la - lb
    return a.instrument.symbol.localeCompare(b.instrument.symbol)
  })
  return matches.slice(0, limit)
}

export async function searchInstruments(
  prefix: string,
  opts: { type?: InstrumentType; limit?: number } = {},
): Promise<InstrumentDetail[]> {
  const trimmed = prefix.trim().toUpperCase()
  if (!trimmed) return []
  const limit = opts.limit ?? 20
  const params: (string | number)[] = [`${trimmed}%`]
  let sql = 'SELECT * FROM instruments WHERE symbol LIKE ?'
  if (opts.type) {
    sql += ' AND type = ?'
    params.push(opts.type)
  }
  sql += ' ORDER BY length(symbol), symbol LIMIT ?'
  params.push(limit)
  let rows: Row[] = []
  try {
    rows = await query<Row>(sql, params)
  } catch {
    rows = []
  }
  // Merge: SQLite persists across sessions, memCache covers rows whose insert
  // hasn't landed yet (or an OPFS-less mobile session).
  const merged = new Map<string, InstrumentDetail>()
  for (const it of searchMemCache(trimmed, opts)) merged.set(it.instrument.symbol, it)
  for (const r of rows) merged.set(r.symbol, rowToInstrument(r))
  const out = [...merged.values()]
  out.sort((a, b) => {
    const la = a.instrument.symbol.length
    const lb = b.instrument.symbol.length
    if (la !== lb) return la - lb
    return a.instrument.symbol.localeCompare(b.instrument.symbol)
  })
  return out.slice(0, limit)
}

export async function clearInstruments(): Promise<void> {
  memCache.clear()
  await exec('DELETE FROM instruments')
}
